import React from 'react';
import {
	ApSimpleSearch,
	ApButtonSecondary,
	ApButtonMain,
	ApLoaderDefault,
} from '@alixpartners/ui-components';
import isEmpty from 'lodash/isEmpty';

import { convertToArray } from 'Details/utils';
import { EMPTY_VALUE } from '../../Details/utils';
import { DelayedList } from './DelayedList';

import { SmallButtonLink } from '../MoreFilters/MoreFilters.styles';
import {
	ButtonWrapper,
	ActionsWrapper,
	SearchInputWrapper,
	Checkbox,
	ButtonLinkWrapper,
	ContainerInner,
	LoaderWrapper,
} from './styles';

export class CustomFilterSelect extends React.Component {
	state = {
		selectedItems: this.getSelectedItemsFromProps(),
	};

	handleApply = async () => {
		const { onChange } = this.props;

		await onChange(this.getParamsFromState());

		if (this.unmounted) return;

		this.setState({
			open: false,
			// TODO: check if updating selectedItems on props change is needed
			selectedItems: this.getSelectedItemsFromProps(),
			searchInputValue: null,
		});
	};

	getParamsFromState = () => {
		const { data, customFilters = [], customFiltersValues = [] } = this.props;

		let { selectedItems } = this.state;

		if (!selectedItems || selectedItems.length === data.values.length) {
			selectedItems = [];
		}

		const customFiltersArray = convertToArray(customFilters);
		const customFiltersValuesArray = convertToArray(customFiltersValues);
		const existingFiltersWithoutCurrent = customFiltersArray
			.map((filterName, index) => ({
				name: filterName,
				value: customFiltersValuesArray[index],
			}))
			.filter(({ name }) => name !== data.name);

		const newFilters = [
			...existingFiltersWithoutCurrent,
			...selectedItems.map(filterValue => ({
				name: data.name,
				value: filterValue,
			})),
		];

		return {
			customFilters: newFilters.map(({ name }) => name),
			customFiltersValues: newFilters.map(({ value }) => value),
		};
	};

	cachedSearchInputValue = null;
	timeoutSetSearchInputValue = null;
	setSearchInputValue = e => {
		this.cachedSearchInputValue = e.target ? e.target.value : null;
		clearTimeout(this.timeoutSetSearchInputValue);
		this.timeoutSetSearchInputValue = setTimeout(
			() => this.setState({ searchInputValue: this.cachedSearchInputValue }),
			200
		);
	};

	componentWillUnmount() {
		clearTimeout(this.timeoutSetSearchInputValue);
		this.unmounted = true;
	}

	// TODO: move to utils
	includes = (text, search) =>
		text.toLowerCase().includes(search.toLowerCase());

	handleSelectAll = options => {
		const { selectedItems: oldSelectedItems, searchInputValue } = this.state;
		let selectedItems = [];

		if (options.length === 0) {
			return;
		}

		if (searchInputValue) {
			if (oldSelectedItems) {
				selectedItems = Array.from(
					new Set([...oldSelectedItems, ...options.map(({ value }) => value)])
				);
			} else {
				selectedItems = options.map(({ value }) => value);
			}
		}

		this.setState({
			selectedItems,
			forceRerenderId: Date.now(),
		});
	};

	handleClear = options => {
		const { selectedItems: oldSelectedItems, searchInputValue } = this.state;
		let selectedItems = null;

		const { data } = this.props;

		if (options.length === 0) {
			return;
		}

		if (searchInputValue) {
			if (oldSelectedItems) {
				if (oldSelectedItems.length === 0) {
					const filteredItems = data.values
						.map(item => item.value)
						.filter(
							item => !this.includes(item || EMPTY_VALUE, searchInputValue)
						);
					selectedItems = filteredItems.length === 0 ? null : filteredItems;
				} else {
					const filteredItems = oldSelectedItems.filter(
						item => !this.includes(item || EMPTY_VALUE, searchInputValue)
					);
					selectedItems = filteredItems.length === 0 ? null : filteredItems;
				}
			}
		}

		this.setState({ selectedItems, forceRerenderId: Date.now() });
	};

	handleCheckboxClick = e => {
		const checkedItem = e.target && e.target.value;
		const { selectedItems } = this.state;
		const { data } = this.props;
		let newSelectedItems = selectedItems ? [...selectedItems] : [];

		if (selectedItems) {
			if (selectedItems.length === 0) {
				newSelectedItems = data.values
					.map(item => item.value || '')
					.filter(item => item !== checkedItem);
			} else {
				if (newSelectedItems.indexOf(checkedItem) !== -1) {
					newSelectedItems = newSelectedItems.filter(
						item => item !== checkedItem
					);
				} else {
					newSelectedItems.push(checkedItem);
				}
			}
		} else {
			newSelectedItems = [checkedItem];
		}

		if (newSelectedItems.length === 0) {
			newSelectedItems = null;
		} else if (newSelectedItems.length === data.values.length) {
			newSelectedItems = [];
		}

		this.setState({ selectedItems: newSelectedItems });
	};

	getSelectedItemsFromProps() {
		const { data, customFilters = [], customFiltersValues = [] } = this.props;

		const customFiltersArray = convertToArray(customFilters);
		const customFiltersValuesArray = convertToArray(customFiltersValues);
		const isActiveFilter = customFiltersArray.includes(data.name);

		const currentValues = isActiveFilter
			? customFiltersArray
					.map((filterName, index) => ({
						name: filterName,
						value: isEmpty(customFiltersValuesArray[index])
							? ''
							: customFiltersValuesArray[index],
					}))
					.filter(({ name }) => name === data.name)
					.map(({ value }) => value)
			: [];

		return currentValues;
	}

	generateOptions = values => {
		const filteredValues = values.filter(({ value }) => value);
		const blankValue =
			filteredValues.length !== values.length
				? [
						{
							label: EMPTY_VALUE,
							value: '',
						},
				  ]
				: [];

		return [...filteredValues, ...blankValue];
	};

	textWidthCanvas = document.createElement('canvas');
	measureText = text => {
		const context = this.textWidthCanvas.getContext('2d');
		context.font = 'normal 16px Roboto';
		const metrics = context.measureText(text);
		return metrics.width;
	};

	render() {
		const { data, closeFilter, loading } = this.props;
		const { searchInputValue, selectedItems, forceRerenderId } = this.state;

		if (!data) {
			return null;
		}

		const selectedItemsMap = selectedItems
			? selectedItems.reduce((map, item) => ({ ...map, [item]: true }), {})
			: [];

		let options = this.generateOptions(data.values);
		const selectedItemsFromProps = this.getSelectedItemsFromProps();
		const isApplyButtonDisabled =
			!selectedItems ||
			(selectedItemsFromProps.length === selectedItems.length &&
				selectedItemsFromProps.filter(item => selectedItemsMap[item]).length ===
					selectedItemsFromProps.length) ||
			loading;

		if (searchInputValue) {
			options = options.filter(({ value, label }) =>
				this.includes(value || label, searchInputValue)
			);
		}

		return (
			<React.Fragment>
				<ButtonLinkWrapper>
					<SmallButtonLink onClick={() => this.handleSelectAll(options)}>
						Select all
					</SmallButtonLink>
					<SmallButtonLink onClick={() => this.handleClear(options)}>
						Clear
					</SmallButtonLink>
				</ButtonLinkWrapper>
				<SearchInputWrapper>
					<ApSimpleSearch
						placeholder="Search for a filter"
						onKeyUp={this.setSearchInputValue}
						onClear={this.setSearchInputValue}
					/>
				</SearchInputWrapper>

				<ContainerInner>
					<DelayedList list={options}>
						{({ value, label }) => (
							<Checkbox
								value={value}
								key={forceRerenderId + value.toString()}
								onChange={this.handleCheckboxClick}
								defaultChecked={
									(selectedItems && selectedItems.length === 0) ||
									!!selectedItemsMap[value]
								}
							>
								{label}
							</Checkbox>
						)}
					</DelayedList>
				</ContainerInner>
				<ActionsWrapper>
					<ButtonWrapper>
						<ApButtonSecondary onClick={closeFilter}>Cancel</ApButtonSecondary>
						<LoaderWrapper>
							{loading && <ApLoaderDefault loaderSize="sm" theme="light" />}
						</LoaderWrapper>
						<ApButtonMain
							disabled={isApplyButtonDisabled}
							onClick={isApplyButtonDisabled ? null : this.handleApply}
						>
							Apply filters
						</ApButtonMain>
					</ButtonWrapper>
				</ActionsWrapper>
			</React.Fragment>
		);
	}
}
